import React, { useState, useRef } from 'react';
import './upgrades-accordion.less';
import { useClassName, useWindowSize } from 'common/hooks';
import { SVGIcon as Icon } from 'common/components';
import { Button } from 'common/lazy/SemanticUI';
import { Swiper, SwiperSlide } from 'swiper/react';
import { usePagination } from 'public/screens/Home/Components/usePagination';
import { Pagination, Autoplay } from 'swiper';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';

export const UpgradesAccordion = ({ slides }) => {
  const className = useClassName('UpgradesAccordion');
  const [currentIndex, setCurrentIndex] = useState(0);
  return (
    <div className={className('container')}>
      {slides.map((slide, index) => (
        <UpgradeSlide
          key={index}
          slide={slide}
          isActive={currentIndex === index}
          onSelect={() => setCurrentIndex(index)}
        />
      ))}
    </div>
  );
};

const UpgradeSlide = ({ slide, isActive, onSelect }) => {
  const className = useClassName('UpgradeSlide');
  const pagination = usePagination({});
  const { isMobile, isTablet } = useWindowSize();
  const [hasMultipleSlides, setHasMultipleSlides] = useState(false);
  const containerRef = useRef(null);

  const handleSwiperInit = (swiper) => {
    const bullets = swiper.pagination.bullets;
    setHasMultipleSlides(bullets.length > 1);
  };

  const handleClick = () => {
    onSelect();

    // En mobile, hacer scroll al inicio del slide cuando se abre
    if ((isMobile || isTablet) && !isActive && containerRef.current) {
      setTimeout(() => {
        const element = containerRef.current;
        const offset = 80; // Offset para el header fijo
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;

        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth',
        });
      }, 100); // Pequeño delay para que la animación de apertura comience primero
    }
  };

  return (
    <div
      ref={containerRef}
      className={className('container', isActive ? 'open' : 'closed')}
      onClick={handleClick}>
      <div className={className('swiper')}>
        <Swiper
          {...pagination}
          autoCorrect="on"
          modules={[Pagination, Autoplay]}
          centeredSlides={isMobile || isTablet ? true : false}
          spaceBetween={0}
          slidesPerView={1}
          speed={500}
          onSwiper={handleSwiperInit}
          preventClicksPropagation={true}
          className={className('carousel')}
          autoplay={{ delay: 3000, disableOnInteraction: false }}
          loop={hasMultipleSlides}>
          {(slide && slide?.items ? slide?.items : []).map((item, index) => {
            return (
              <SwiperSlide key={index} className={className('carousel__slide')}>
                <img src={item.desktop.assetUrl} alt={item.desktop.assetAlt} />
              </SwiperSlide>
            );
          })}
        </Swiper>
      </div>
      <div className={className('inner')}>
        <p className={className('type')}>{slide.subtitle}</p>
        <h3 className={className('title')}>{slide.title}</h3>
        <p className={className('description')}>
          {documentToReactComponents(slide.description)}
        </p>
        <div className={className('amenities')}>
          {slide.features.map((feature, index) => (
            <div key={index} className={className('amenities__item')}>
              <div key={index} className={className('amenities__item__icon')}>
                <Icon name={feature.icon} size="small" />
              </div>
              <p className={className('amenities__item__text')}>
                {documentToReactComponents(feature.text)}
              </p>
            </div>
          ))}
        </div>
        <Button
          className={className('button')}
          content={slide.link?.label || 'Explore more'}
          primary
          href={slide.link?.url || '#'}
          target={slide.link?.type === 'external' ? '_blank' : '_self'}
        />
        <div className={className('plus-button')}>
          <Icon name="add-minimal" size="small" />
        </div>
      </div>
    </div>
  );
};
